import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import { useNavigate } from "react-router-dom";
import CheckDependencyModal from "../../components/CheckDependencyModal";

function OrganizationLevelDetail() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const navigate = useNavigate();

  const [triggerRerender, setTriggerRerender] = useState(false);
  const [showDependencyModal, setShowDependencyModal] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      levelDetails: [
        {
          id: 0,
          name: "",
          comments: "",
          createdBy: localStorage.getItem("userId"),
          accountLength: localStorage.getItem("accountLength"),
          organizationLevelId: "",
        },
      ],
    },
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "levelName",
        header: "Name",
        // size: 100,
      },
      {
        accessorKey: "levelValue",
        header: "Value",
        // size: 100,
      },
      {
        accessorKey: "comments",
        header: "Comments",
        // size: 200,
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEditClick(row.original.id)}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDeleteClick(row.original.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = organizationLevelsDetail.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.levelName, // Replace with the correct property name
      ID: row.id,
      Value: row.levelValue, // Ensure this property exists
      Comments: row.comments, // Ensure this property exists
    }));

    console.log(filteredData);
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Organization Level Details"
    );
    XLSX.writeFile(workbook, "OrganizationLevelDetails.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  /* eslint-disable no-unused-vars */
  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const domain = (process.env.REACT_APP_LIVE_DOMAIN_URL || "").replace(/\/$/, "");
    
    const cleanPath = (path) => (path ? path.replace(/^\/+/, "") : "");
    const orgLogo = orgLogoPath ? `${domain}/ExternalImages/${cleanPath(orgLogoPath)}` : null;
    const centerLogo = centerLogoPath ? `${domain}/ExternalImages/${cleanPath(centerLogoPath)}` : null;

    let logoToUse = logo; // Default imported logo
    let sidebarLogoUrl = null;

    if (roleId == "2") sidebarLogoUrl = orgLogo;
    else if (roleId == "3") sidebarLogoUrl = centerLogo;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // try {
    //     doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);
    // } catch (err) {
    //     console.error("Error adding image to PDF:", err);
    //     if (logoToUse !== logo) {
    //          doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
    //     }
    // }

    const title = "Organization Level Details";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter((column) => column.accessorKey !== "actions");

    const headers = includedColumns.map((col) => col.header);

    const bodyData = organizationLevelsDetail.map((row, index) => {
      return includedColumns.map((column) => {
          if (column.accessorKey === 'mrt-row-numbers') return index + 1;
          return row[column.accessorKey] || "";
      });
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("OrganizationLevelDetails.pdf");
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const [organizationLevels, setOrganizationLevels] = useState([]);
  const [organizationLevelsDetail, setOrganizationLevelsDetail] = useState([]);


  // Fetch organization levels (for dropdown)
  useEffect(() => {
    const fetchOrganizationLevels = async () => {
      try {
        const response = await get(
          `OrganizationLevel/GetOrganizationLevelsByOrganization?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const levels = response?.levels || [];
        setOrganizationLevels(levels);

        if (levels.length === 0) {
          setShowDependencyModal(true);
        }
      } catch (error) {
        console.error("Error fetching GetOrganizationLevels:", error);
      }
    };

    fetchOrganizationLevels();
  }, [setValue, watch]);

  // Fetch organization level details (for populating the form)
  useEffect(() => {
    const fetchOrganizationLevelsDetail = async () => {
      try {
        const response = await get(
          `OrganizationLevel/GetOrganizationLevelsDetailByOrganization?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setOrganizationLevelsDetail(response.levelDetail);
      } catch (error) {
        console.error(
          "Error fetching GetOrganizationLevelsDetailByOrganization:",
          error
        );
      }
    };

    fetchOrganizationLevelsDetail();
  }, [triggerRerender]);

  const handleEditClick = async (rowId) => {
    const singleLevelDetailData = await get(
      `OrganizationLevel/GetOrganizationLevelsDetailById?levelDetailId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const singleLevelDetailDataLevelDetail = singleLevelDetailData.levelDetail;

    // Find the selected level from organizationLevels based on the organizationLevelId
    const selectedLevel = organizationLevels.find(
      (level) =>
        level.id === singleLevelDetailDataLevelDetail.organizationLevelId
    );

    reset({
      levelDetails: [
        {
          id: singleLevelDetailDataLevelDetail.id || 0,
          name: singleLevelDetailDataLevelDetail.name || "",
          comments: singleLevelDetailDataLevelDetail.comments || "",
          accountLength: singleLevelDetailDataLevelDetail.accountLength,
          createdBy: singleLevelDetailDataLevelDetail.createdBy,
          updatedBy: localStorage.getItem("userId"),
          organizationLevelId: {
            value: selectedLevel?.id || "",
            label: selectedLevel?.name || "",
          },
        },
      ],
    });
  };

  const handleDeleteClick = async (rowId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this level's detail?"
    );

    if (confirmDelete) {
      const formData = {
        id: rowId,
        isDelete: true,
        deletedBy: localStorage.getItem("userId"),
      };

      try {
        const response = await post(
          `OrganizationLevel/DeleteOrganizationLevelDetailsById`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.isSuccess) {
          toast.success(response.message || "Success!");
          setTriggerRerender((prev) => !prev);
        } else {
          toast.error(response.message || "An error occurred while deleting.");
        }
      } catch (error) {
        console.error("Error deleting category:", error);
      }
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "levelDetails",
  });

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = data.levelDetails.map((detail) => ({
      ...detail,
      organizationLevelId: detail.organizationLevelId.value || 0,
    }));

    try {
      const response = await post(
        "OrganizationLevel/UpdateOrganizationLevelDetailsAsync",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
        reset({
          levelDetails: [
            {
              id: 0,
              name: "",
              comments: "",
              createdBy: localStorage.getItem("userId"),
              accountLength: localStorage.getItem("accountLength"),
            },
          ],
        });
        setTriggerRerender((prev) => !prev);
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  const handleOkButtonClick = () => {
    setShowDependencyModal(false);
    navigate("/organization-levels");
  };



  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Organization Level Details</h2>

        {/* <div className="ms-auto">
          <Link
            to="/organization-form"
            className="btn btn-outline-primary btn-sm"
          >
            Add New
          </Link>
        </div> */}
      </div>

      <form className="row g-3" onSubmit={handleSubmit(saveData)}>
        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <div className="row g-2">
                <div className="col">
                  <label className="form-label">
                    Name<span className="text-danger fs-9">*</span>
                  </label>
                </div>
                <div className="col">
                  <label className="form-label">
                    Value<span className="text-danger fs-9">*</span>
                  </label>
                </div>
                <div className="col-3">
                  <label className="form-label">Comments</label>
                </div>
                <div className="col-2">
                  <label className="form-label">Action</label>
                </div>
              </div>

              {fields.map((field, index) => (
                <div className="row g-2 mb-2" key={field.id}>
                  <div className="col">
                    <Controller
                      name={`levelDetails[${index}].organizationLevelId`}
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={organizationLevels.map((level) => ({
                            value: level.id,
                            label: level.name,
                          }))}
                          // value={field.value || null} // Ensure correct value is set from API response
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption); // Handle form state update
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          isClearable
                        />
                      )}
                    />
                    {errors.levelDetails?.[index]?.organizationLevelId && (
                      <small className="text-danger">
                        {errors.levelDetails[index].organizationLevelId.message}
                      </small>
                    )}
                  </div>
                  <div className="col">
                    <input
                      {...register(`levelDetails[${index}].name`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck(
                        `levelDetails[${index}].name`
                      )}
                    />
                    {errors.levelDetails?.[index]?.name && (
                      <small className="text-danger">
                        {errors.levelDetails[index].name.message}
                      </small>
                    )}
                  </div>
                  <div className="col-3">
                    <input
                      {...register(
                        `levelDetails[${index}].comments`,
                        {
                          maxLength: {
                            value: 250,
                            message: "Maximum of 250 characters allowed.",
                          },
                        }
                        //   {
                        //   required: "This field is required.",
                        // }
                      )}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck(
                        `levelDetails[${index}].comments`
                      )}
                    />
                    {errors.levelDetails?.[index]?.comments && (
                      <small className="text-danger">
                        {errors.levelDetails[index].comments.message}
                      </small>
                    )}
                  </div>
                  <div className="col-2">
                    <button
                      className="btn btn-outline-primary py-1 px-2 lh-2"
                      type="button"
                      onClick={() =>
                        append({
                          id: 0,
                          createdBy: localStorage.getItem("userId"),
                          // organizationLevelId: 16,
                          name: "",
                          comments: "",
                        })
                      }
                    >
                      <span className="fas fa-plus"></span>
                    </button>
                    {fields.length > 1 && (
                      <button
                        className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                        type="button"
                        onClick={() => remove(index)}
                      >
                        <span className="fas fa-minus"></span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <div className="mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Save" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-6">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={organizationLevelsDetail}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </form>

      {showDependencyModal && (
        <CheckDependencyModal
          title="No Levels Found"
          message="Please create at least one level before proceeding to the level details."
          onClose={() => setShowDependencyModal(false)}
          okButtonLabel="Go to Levels"
          okButtonClick={handleOkButtonClick}
        />
      )}
    </>
  );
}

export default OrganizationLevelDetail;
